import { normalizeBorrowPositionUsdValue } from '@/utils/borrowMarkets/normalizeBorrowPositionUsdValue'
import {
  type BorrowMarketAssetPosition,
  type BorrowMarketPathParams,
  type BorrowPositionAsset,
  getInternalAppSdk,
} from '@/utils/internalAppSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { Address } from 'viem'

const borrowMarketPositionQueryKey = ['borrowMarketPosition'] as const

interface BorrowMarketPositionQueryParams extends BorrowMarketPathParams {
  address: Address | undefined
  assetId: string
}

interface BorrowMarketPositionAssetBalance
  extends Omit<BorrowPositionAsset, 'balanceUsd' | 'suppliedUsd' | 'borrowedUsd'> {
  balanceUsd: number
  suppliedUsd: number
  borrowedUsd: number
}

export interface BorrowMarketAssetPositionBalance extends Omit<BorrowMarketAssetPosition, 'asset'> {
  asset: BorrowMarketPositionAssetBalance
  suppliedUsd: number
  borrowedUsd: number
  netUsd: number
}

function borrowMarketPositionQueryOptions({ address, network, marketId, assetId }: BorrowMarketPositionQueryParams) {
  return queryOptions({
    queryKey: [...borrowMarketPositionQueryKey, { userAddress: address, network, marketId, assetId }] as const,
    async queryFn({ queryKey }) {
      const { userAddress, network, marketId, assetId } = queryKey[1]
      if (!userAddress) return null
      const internalSdk = getInternalAppSdk()

      return await internalSdk.getBorrowMarketAssetPosition({
        userAddress,
        network,
        marketId,
        assetId,
      })
    },
    select(data): BorrowMarketAssetPositionBalance | null {
      if (data == null) {
        return null
      }

      const asset = {
        ...data.asset,
        balanceUsd: normalizeBorrowPositionUsdValue(data.asset.balanceUsd),
        suppliedUsd: normalizeBorrowPositionUsdValue(data.asset.suppliedUsd),
        borrowedUsd: normalizeBorrowPositionUsdValue(data.asset.borrowedUsd),
      }

      return {
        ...data,
        asset,
        suppliedUsd: asset.suppliedUsd,
        borrowedUsd: asset.borrowedUsd,
        netUsd: asset.suppliedUsd - asset.borrowedUsd,
      }
    },
    enabled: !!address,
    retry: 2,
    staleTime: 1 * MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: true,
    placeholderData: undefined,
  })
}

export const useBorrowMarketPositionQuery = (params: BorrowMarketPositionQueryParams) => {
  const { data, isError, isFetching } = useQuery({
    ...borrowMarketPositionQueryOptions(params),
  })

  return { data, isError, isFetching }
}
